import { Avatar } from '@/utils/types';
import useGetDashboardCounterFormatQuestion175 from '@/hooks/useGetDashboardCounterFormatQuestion175';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface Props {
    avatar: Avatar;
}

interface ItemProps {
    title: string;
    label0: string;
    value0: number;
    label1: string;
    value1: number;
}

const Item = (props: ItemProps) => {
    return (
        <Box
            sx={{
                display: 'grid',
                gap: 1,
                justifyItems: 'center',
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    justifyItems: 'center',
                    border: '4px solid #505EB6',
                    borderRadius: 5,
                    padding: 1,
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 600,
                        color: '#505EB6',
                    }}
                >
                    {props.label0}
                </Typography>
                <Typography
                    sx={{
                        fontWeight: 700,
                    }}
                >
                    {props.value0}
                </Typography>
                <Typography
                    sx={{
                        fontWeight: 600,
                        color: '#505EB6',
                    }}
                >
                    {props.label1}
                </Typography>
                <Typography
                    sx={{
                        fontWeight: 700,
                    }}
                >
                    {props.value1}
                </Typography>
            </Box>

            <Typography
                sx={{
                    fontWeight: 600,
                    color: '#505EB6',
                }}
            >
                {props.title}
            </Typography>
        </Box>
    );
};

const SimpleCounterDashboard = (props: Props) => {
    const rootId =
        typeof props.avatar.root === 'object'
            ? (props.avatar.root.id as string)
            : (props.avatar.id.split('/')[3] as string);

    const tookIntoAccountForDisorder = useGetDashboardCounterFormatQuestion175({
        metadata_avatar_type__alphaId: 'big:type:disorder',
        node_name: 'Prise en compte',
        avatar_left: props.avatar.lft as number,
        avatar_level: props.avatar.lvl as number,
        avatar_right: props.avatar.rgt as number,
        avatar_root_id: rootId,
    });

    const toEvaluateForDisorder = useGetDashboardCounterFormatQuestion175({
        metadata_avatar_type__alphaId: 'big:type:disorder',
        node_name: 'A évaluer',
        avatar_left: props.avatar.lft as number,
        avatar_level: props.avatar.lvl as number,
        avatar_right: props.avatar.rgt as number,
        avatar_root_id: rootId,
    });

    const tookIntoAccountForTreatment = useGetDashboardCounterFormatQuestion175({
        metadata_avatar_type__alphaId: 'big:type:treatment',
        node_name: 'Prise en compte',
        avatar_left: props.avatar.lft as number,
        avatar_level: props.avatar.lvl as number,
        avatar_right: props.avatar.rgt as number,
        avatar_root_id: rootId,
    });

    const toEvaluateForTreatment = useGetDashboardCounterFormatQuestion175({
        metadata_avatar_type__alphaId: 'big:type:treatment',
        node_name: 'A évaluer',
        avatar_left: props.avatar.lft as number,
        avatar_level: props.avatar.lvl as number,
        avatar_right: props.avatar.rgt as number,
        avatar_root_id: rootId,
    });

    const askedForAudit = useGetDashboardCounterFormatQuestion175({
        metadata_avatar_type__alphaId: 'big:type:audit',
        node_name: 'Audit',
        avatar_left: props.avatar.lft as number,
        avatar_level: props.avatar.lvl as number,
        avatar_right: props.avatar.rgt as number,
        avatar_root_id: rootId,
    });

    const takeIntoAccountForAudit = useGetDashboardCounterFormatQuestion175({
        metadata_avatar_type__alphaId: 'big:type:audit',
        node_name: 'Pris en compte',
        avatar_left: props.avatar.lft as number,
        avatar_level: props.avatar.lvl as number,
        avatar_right: props.avatar.rgt as number,
        avatar_root_id: rootId,
    });

    return (
        <Box
            sx={{
                display: 'grid',
                gap: 2,
                justifyContent: 'start',
                justifyItems: 'center',
            }}
        >
            <Typography
                sx={{
                    fontWeight: 700,
                }}
            >
                Compteur de signalement
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, auto)',
                    gap: 3,
                }}
            >
                <Item
                    title={'Désordres'}
                    label0={'A évaluer'}
                    value0={toEvaluateForDisorder.value}
                    label1={'Prise en compte'}
                    value1={tookIntoAccountForDisorder.value}
                />
                <Item
                    title={'Traitements'}
                    label0={'A évaluer'}
                    value0={toEvaluateForTreatment.value}
                    label1={'Prise en compte'}
                    value1={tookIntoAccountForTreatment.value}
                />
                <Item
                    title={'Audits'}
                    label0={'Demandé'}
                    value0={askedForAudit.value}
                    label1={'Pris en compte'}
                    value1={takeIntoAccountForAudit.value}
                />
            </Box>
        </Box>
    );
};

export default SimpleCounterDashboard;
